// v0.6 — Inline action for a card whose answer is an exchange rate in Step 2.
//
// Jumps to Step 2 and highlights the FX section that actually clears the
// flagged rows. A national-bucket row (LOCAL_SALE/REFUND_*) invoiced in a
// foreign currency is cleared ONLY by the per-country monthly grid
// (MonthlyFxRates, [data-testid="monthly-fx-rates"]); an OSS/UK-bucket row is
// cleared by the flat regime rate (PeriodFxRates, [data-testid="period-fx-
// rates"]). The backend group payload (fx_national_count / fx_oss_count /
// fx_pairs) says which applies per group, so we target the right box — and
// name the exact source→target pair(s) instead of the generic prompt.
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { useWorkflowOptional } from "../../workflow/WorkflowContext";

const highlight = (testid) => {
  let tries = 0;
  const step = () => {
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-amber-400");
      setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 2000);
      return;
    }
    if (tries < 25) { tries += 1; setTimeout(step, 120); }
  };
  setTimeout(step, 120);
};

export const MissingRateAction = ({ flagType, group }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wf = useWorkflowOptional();
  // A2-582: drawn wherever the server's answer says "enter the exchange rate
  // in Step 2" (GroupAnswers), not chosen here by the card's flag name.

  const nat = group?.fx_national_count || 0;
  const oss = group?.fx_oss_count || 0;
  // Which FX section(s) actually clear these rows. National grid first when
  // present (the case the old code wrongly sent to the flat box).
  const targets = [];
  if (nat > 0) targets.push("monthly-fx-rates");
  if (oss > 0) targets.push("period-fx-rates");
  if (targets.length === 0) targets.push("period-fx-rates"); // safe default

  // Name the exact pair(s): "Needs CHF→EUR" / "Needs USD→DKK, GBP→DKK".
  const pairs = [...new Set((group?.fx_pairs || []).map((p) => p.pair))];
  const pairLabel = pairs.length
    ? t("ui.review_grouped.fx_needs_pairs", {
        pairs: pairs.join(", "),
        defaultValue: `Needs ${pairs.join(", ")}`,
      })
    : null;

  const goToStep2 = () => {
    if (wf?.setActiveStep) {
      wf.setActiveStep(2);
      targets.forEach(highlight);
    } else {
      navigate("/workflow/configure");   // A2-581 lead 22: ?step= was read nowhere
    }
  };

  return (
    <div className="px-8 pb-3 -mt-1 space-y-1">
      {pairLabel && (
        <div
          data-testid={`rg-fx-pairs-${flagType}`}
          className="tabular-nums text-[11px] font-bold text-amber-800 dark:text-amber-200"
        >
          {pairLabel}
        </div>
      )}
      <button
        type="button"
        data-testid={`rg-action-open-step-2-${flagType}`}
        data-fx-target={targets.join(",")}
        onClick={goToStep2}
        className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white"
      >
        {nat > 0
          ? t("ui.review_grouped.open_step_2_monthly", {
              defaultValue: "Open Step 2 — enter monthly rate",
            })
          : t("ui.review_grouped.open_step_2", {
              defaultValue: "Open Step 2 — enter exchange rate",
            })}
        <ArrowRight size={12} weight="bold" />
      </button>
    </div>
  );
};

export default MissingRateAction;
